import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { get, random, set } from 'lodash';
import classNames from 'classnames';
import { Button, Dropdown, DropdownButton } from 'react-bootstrap';
import DocumentScan from 'mdi-react/ScanHelperIcon';
import SaveIcon from 'mdi-react/ContentSaveOutlineIcon';
import RotateRightVariantIcon from 'mdi-react/RotateRightVariantIcon';

import ModalWrapper from 'modules/shared/ModalWrapper';
import { trims } from 'utils';
import { cleanScan, cleanScanImg, getScanError, getScanRequest } from 'modules/common/ScanerBtn/actions';
import TopBorderBtn from 'modules/shared/TopBorderBtn';
import { scanerSelector } from 'modules/common/ScanerBtn/selectors';
import { peripheralsSelector } from 'modules/common/Header/selectors';
import { SliderImage } from '../../../../../entities/Photo/interfaces';
import { Trip } from '../../../../../entities/Trip';
import { dataUrlToFile } from '../../../../../entities/Document';
import { Peripherals } from '../../../../../entities/Peripherals/interfaces';
import Carousel from '../Carousel';

import { getCroppedImg, getRotatedImage } from './utils';
import styles from './ScanerModal.module.scss';

const ScanerModal: React.FC = () => {
  const dispatch = useDispatch();
  const formik = useFormikContext<Trip>();
  const { values, setFieldValue } = formik;

  const peripherals = useSelector(peripheralsSelector);
  const { typeDoc, scan, index, loading, error } = useSelector(scanerSelector);

  const [preview, setPreview] = useState<SliderImage[]>([]);
  const [fieldName, setFieldName] = useState('');
  const [scannerUrl, setScannerUrl] = useState('');
  const [format, setFormat] = useState<string | null>('A6');
  const [idx, setIdx] = useState(0);

  const windowUrl = window.URL || window.webkitURL;

  const handlerClose = () => {
    setPreview([]);
    setFieldName('');
    dispatch(cleanScan());
  };

  const saveHandler = async () => {
    const img = preview[idx];
    const { picture } = img;
    // @ts-ignore
    const images = get(values, fieldName, []);

    const pic = await dataUrlToFile(picture, 'image/jpeg');

    setFieldValue(fieldName, [...images, pic]);

    set(preview[idx], 'isSave', true);
    const curIdx = preview.findIndex((p) => p.id === preview[idx].id);

    setIdx(curIdx);
    setPreview(preview);
  };

  const handlerGetA4 = () => {
    setIdx(0);
    dispatch(getScanRequest(scannerUrl));
  };

  const crop = async (img: SliderImage | null) => {
    const src = img ? img.picture : preview[idx].picture;
    const croppedImage = await getCroppedImg(src);

    const curImg = img ? img : preview[idx];
    set(curImg, 'picture', croppedImage);
    set(curImg, 'isCrop', true);
    set(curImg, 'picIndex', 0);

    const filter = preview.filter((p) => p.id !== curImg.id);

    setPreview([curImg, ...filter]);
    setIdx(0);
    dispatch(cleanScanImg());
  };

  const rotate = async () => {
    const rotatedImg = await getRotatedImage(preview[idx].picture);

    const curImg = preview[idx];
    set(curImg, 'picture', rotatedImg);
    set(curImg, 'picIndex', 0);
    const filter = preview.filter((p) => p.id !== curImg.id);

    setPreview([curImg, ...filter]);
    setIdx(0);
  };

  const getReqNumDocs = () => {
    switch (preview[idx].type) {
      case 'vehicle':
        return 1;
      case 'trailer':
        return 1;
      case 'driver': {
        if (!preview[idx].docIndex) return 2;
        return 1;
      }
      case 'forwarder':
        return 1;
      default:
        break;
    }
  };
  const getLabeleDocs = () => {
    switch (preview[idx].type) {
      case 'vehicle':
        return 'Номер техпаспорту автомобіля: ';
      case 'trailer':
        return 'Номер техпаспорту причепу: ';
      case 'driver': {
        if (!preview[idx].docIndex) return 'Номер посвідчення водія: ';
        return 'Номер паспорту водія: ';
      }
      case 'forwarder':
        return 'Номер паспорту експедитора: ';
      default:
        break;
    }
  };

  useEffect(() => {
    if (!preview.length && scannerUrl.length && !scan) handlerGetA4();

    if (!!scan && !!scan.size) {
      let docNumber = '';

      if (index === null) {
        // @ts-ignore
        docNumber = get(values[typeDoc].document, 'number');
      } else {
        // @ts-ignore
        docNumber = get(values[typeDoc].documents[index], 'number');
      }

      let url = '';
      if ('srcObject' in scan) {
        // @ts-ignore
        scan.srcObject = url;
      } else {
        url = windowUrl.createObjectURL(scan);
      }

      if (format === 'A6') {
        crop({
          id: random(1, 100),
          isSave: false,
          isCrop: false,
          type: typeDoc || '',
          docIndex: index,
          picIndex: idx,
          docNumber,
          name: scan?.name,
          picture: url,
        });
      } else {
        setPreview([
          {
            id: random(1, 100),
            isSave: false,
            isCrop: false,
            type: typeDoc || '',
            docIndex: index,
            picIndex: idx,
            docNumber,
            name: scan?.name,
            picture: url,
          },
          ...preview,
        ]);

        dispatch(cleanScanImg());
      }
    }
  }, [scan, preview, scannerUrl]);

  useEffect(() => {
    if (index === null) {
      setFieldName(`${typeDoc}.document.pictureFiles`);
    } else {
      setFieldName(`${typeDoc}.documents[${index}].pictureFiles`);
    }

    if (peripherals && !!peripherals.length && !scannerUrl.length) {
      const url = peripherals.find((p: Peripherals) => trims(p.type) === 'ImageScanner');

      if (url) return setScannerUrl(url.peripheralUrl);
      dispatch(getScanError(Error('Посилання сканера не знайдено. Зверніться до адміністратора.')));
    }
  }, [peripherals]);

  return (
    <>
      <ModalWrapper
        contentClassName={styles.modal}
        show
        onHide={handlerClose}
        size="lg"
        headerBtn={
          <div>
            {preview.length && !loading ? (
              <a href={preview[idx].picture} download>
                <TopBorderBtn className="mt-2" icon="download" disabled={!preview} />
              </a>
            ) : null}
          </div>
        }
      >
        {error && !loading && (
          <div className={styles.scaning_wait}>
            <h2 className={styles.errorMsg}>{error.message}</h2>
            <Button className={styles.btnLight} variant="light" onClick={handlerGetA4} disabled={loading}>
              Сканувати <DocumentScan />
            </Button>
          </div>
        )}

        {loading && (
          <div className={classNames(styles.scaning_wait, styles.loading)}>
            <h1>Сканування фото... Зачекайте</h1>
          </div>
        )}

        {!!preview.length && !loading && !error && (
          <div className={classNames(styles.scaning_wait, 'd-flex align-items-center flex-column position-relative')}>
            <h3 className={styles.nameDocAbs}>
              {getLabeleDocs()}
              {preview[idx].docNumber}
            </h3>

            <p className={styles.numberDocAbs}>
              Збережено сканованих зображень {preview.filter((p) => p.isSave).length} з {getReqNumDocs()}{' '}
              обов&apos;язкових
            </p>

            <Carousel index={idx} setIndex={setIdx} clearCanvas={!!true} images={preview} withControls />

            <div className={styles.footerGroupBtn}>
              <DropdownButton
                menuAlign="right"
                className={styles.btnDropLight}
                title={format}
                variant="light"
                onSelect={setFormat}
              >
                <Dropdown.Item eventKey="A6" id="A6">
                  A6
                </Dropdown.Item>
                <Dropdown.Item eventKey="A4" id="A4">
                  A4
                </Dropdown.Item>
              </DropdownButton>
              <Button className={styles.btnLight} variant="light" onClick={handlerGetA4} disabled={loading}>
                Сканувати <DocumentScan />
              </Button>
              {preview[idx] ? (
                <>
                  <Button className={styles.btnLight} variant="light" onClick={rotate} disabled={preview[idx]?.isSave}>
                    Повернути <RotateRightVariantIcon />
                  </Button>
                  <Button
                    className={styles.footerBtn}
                    variant="success"
                    onClick={saveHandler}
                    disabled={preview[idx]?.isSave}
                  >
                    Зберегти <SaveIcon />
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        )}
      </ModalWrapper>
    </>
  );
};

export default ScanerModal;
